'use client';

import { useCartInfoAtom } from '@/state';
import { hasCartMergePending, mergeCartWithNotification } from '@/utils/mergeCartWithNotification';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * ローカルカートのバージョン整合性チェック + マージ失敗時のセーフティネット。
 *
 * - `NEXT_PUBLIC_LOCAL_CART_VERSION` が変わったら localStorage をクリアする
 * - `cartMergePending` フラグが立っている = `useSignInWithCartMerge` でのマージが恒久失敗
 *   している場合に、ログイン中 & ローカルカートあり の条件で再試行する。
 */
function GfCheckCart({ isLogin }: { isLogin: boolean }) {
  const [cartInfo, , clearCartInfo] = useCartInfoAtom();
  const router = useRouter();
  const retryingRef = useRef(false);

  useEffect(() => {
    const isOldVersion = () => {
      if (!cartInfo || !cartInfo.cartVersion) return true;
      return cartInfo.cartVersion !== Number(process.env.NEXT_PUBLIC_LOCAL_CART_VERSION || 1);
    };

    if (isOldVersion()) {
      clearCartInfo();
    }
    // 30日経過したアイテムの削除はAPI側（hasExpiredItems）に委譲
    // cartVersion のみを依存に絞ることでカート追加のたびに再実行されないようにする
  }, [cartInfo?.cartVersion, clearCartInfo]);

  useEffect(() => {
    const retryPendingMerge = async () => {
      if (retryingRef.current) return;
      if (!isLogin) return;
      if (!hasCartMergePending()) return;
      const items = cartInfo?.items ?? [];
      // atomWithStorage の hydration race（atom 初期値が空 → 後で localStorage 同期）で
      // 誤って空判定する可能性があるため、items 空時はフラグを保持して何もしない。
      // フラグのクリアは次回ログイン時に signInAndMerge 側で行う。
      if (items.length === 0) return;

      retryingRef.current = true;
      try {
        await mergeCartWithNotification(items, clearCartInfo);
        router.refresh();
      } catch (error) {
        // 再試行も失敗。フラグは保持し、次回ページ遷移でまた試す
        Sentry.captureException(error);
      } finally {
        retryingRef.current = false;
      }
    };
    retryPendingMerge();
  }, [isLogin, cartInfo, clearCartInfo, router]);

  return <></>;
}

export default GfCheckCart;
